import { FormEvent, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { FloatingField } from '../../components/FloatingField';
import { Input } from '../../components/Input';
import { RateCodeClearButton, RateCodeFieldWrapper } from './RateCodeField.styles';

export interface RateCodeFieldProps {
  value?: string;
  placeholder: string;
  applyLabel: string;
  clearLabel?: string;
  onApply: (code: string) => void;
  onClear?: () => void;
}

export function RateCodeField({
  value = '',
  placeholder,
  applyLabel,
  clearLabel,
  onApply,
  onClear,
}: RateCodeFieldProps) {
  const [draft, setDraft] = useState(value);
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      setDraft(value);
    }
  }, [value]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    onApply(trimmed);
  };

  const handleClear = () => {
    setDraft('');
    onClear?.();
  };

  const canClear = Boolean(onClear && draft);

  return (
    <RateCodeFieldWrapper onSubmit={handleSubmit}>
      <FloatingField label={placeholder} hasValue={Boolean(draft)} required={false}>
        <Input
          variant="borderless"
          type="text"
          value={draft}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          autoComplete="off"
        />
      </FloatingField>
      {canClear && (
        <RateCodeClearButton type="button" aria-label={clearLabel ?? 'Clear'} onClick={handleClear}>
          ×
        </RateCodeClearButton>
      )}
      <Button variant="neutral" size="small" type="submit" disabled={!draft.trim()}>
        {applyLabel}
      </Button>
    </RateCodeFieldWrapper>
  );
}
